/**
 * ShareButton — Spec 128 desktop companion
 *
 * Renders a button that opens:
 *   - a fixed-position popover with ShareSheet (mode="panel") on desktop (≥ 640px)
 *   - a full modal with ShareSheet (mode="modal") on mobile (< 640px)
 *
 * Viewport is measured at click time — no resize listeners needed.
 * Popover uses position:fixed + getBoundingClientRect() to avoid overflow:hidden clipping.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { ShareSheet } from './ShareSheet';
import type { ShareEntityType, PendingShare, SharePermission } from './ShareSheet';
import { Button } from '../ui';

interface ShareButtonProps {
  entityType: ShareEntityType;
  entityId?: string | number;
  entityTitle: string;
  shareCount?: number;
  pendingShares?: PendingShare[];
  onPendingSharesChange?: (shares: PendingShare[]) => void;
  onShareSuccess?: () => void;
  showPermissions?: boolean;
  defaultExpanded?: boolean;
  /** Extra classes on the trigger button */
  className?: string;
  children?: React.ReactNode;
}

interface PopoverPosition {
  top: number;
  left: number;
}

const POPOVER_WIDTH = 288;
const POPOVER_HEIGHT_ESTIMATE = 400;
const POPOVER_GAP = 6; // px between button and popover

export const ShareButton: React.FC<ShareButtonProps> = ({
  entityType,
  entityId,
  entityTitle,
  shareCount,
  pendingShares,
  onPendingSharesChange,
  onShareSuccess,
  showPermissions,
  defaultExpanded,
  className,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => {
    const mobile = window.innerWidth < 640;
    setIsMobile(mobile);

    if (!mobile && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Default: align right edge of popover to right edge of button, below button
      let left = rect.right - POPOVER_WIDTH;
      let top = rect.bottom + POPOVER_GAP;

      // Flip left if popover would overflow left edge
      if (left < 8) {
        left = rect.left;
      }

      // Flip up if popover would overflow bottom edge
      if (top + POPOVER_HEIGHT_ESTIMATE > viewportHeight - 8) {
        top = rect.top - POPOVER_HEIGHT_ESTIMATE - POPOVER_GAP;
      }

      // Clamp to viewport bounds
      left = Math.max(8, Math.min(left, viewportWidth - POPOVER_WIDTH - 8));
      top = Math.max(8, top);

      setPosition({ top, left });
    }

    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, isMobile, close]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const hasBadge = typeof shareCount === 'number' && shareCount > 0;

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        className={twMerge('relative inline-flex items-center gap-1.5', className)}
        onClick={open}
        aria-label={`Share ${entityTitle}${hasBadge ? ` (${shareCount} shared)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {children ?? (
          <span className="text-sm font-medium text-text-secondary hover:text-text-primary transition-colors">
            Share
          </span>
        )}
        {hasBadge && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-4 text-center bg-[var(--theme-brand-gold)] text-[var(--theme-ink-on-accent)]"
            aria-hidden="true"
          >
            {shareCount! > 99 ? '99+' : shareCount}
          </span>
        )}
      </Button>

      {/* Desktop popover — rendered in a portal to escape overflow:hidden */}
      {isOpen && !isMobile &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Share ${entityTitle}`}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: POPOVER_WIDTH,
              zIndex: 9999,
            }}
            className="rounded-lg border border-border-light bg-bg-primary shadow-xl overflow-hidden"
          >
            <ShareSheet
              entityType={entityType}
              entityId={entityId}
              entityTitle={entityTitle}
              mode="panel"
              isOpen={true}
              onClose={close}
              pendingShares={pendingShares}
              onPendingSharesChange={onPendingSharesChange}
              onShareSuccess={() => {
                onShareSuccess?.();
                close();
              }}
              showPermissions={showPermissions}
              defaultExpanded={defaultExpanded}
            />
          </div>,
          document.body,
        )
      }

      {/* Mobile modal */}
      {isOpen && isMobile && (
        <ShareSheet
          entityType={entityType}
          entityId={entityId}
          entityTitle={entityTitle}
          mode="modal"
          isOpen={true}
          onClose={close}
          pendingShares={pendingShares}
          onPendingSharesChange={onPendingSharesChange}
          onShareSuccess={() => {
            onShareSuccess?.();
            close();
          }}
          showPermissions={showPermissions}
          defaultExpanded={defaultExpanded}
        />
      )}
    </>
  );
};

export type { ShareEntityType, PendingShare, SharePermission };
